import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, CircularProgress, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { green } from '@mui/material/colors';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AvlTable } from '../../Shared/Table/Table';
import styles from './StatusTable.module.css';
import { datetimeToFormattedString } from '../../../Utils/Utils';

export const Columns = {
    run: 'Run',
    averageProgress: 'Average Progress',
    createdAt: 'Creation Date',
    action: 'Actions',
};

function StatusTable(
    {
        onRunDelete,
        results,
    },
) {
    const { isAdmin } = useSelector((state) => state.auth);
    const percentPrecision = 0;
    const myRefs = useRef([]);
    const myLoadingRefs = useRef([]);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(1);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleDelete = async (documentId, index) => {
        myRefs.current[index].style.disabled = true;
        myLoadingRefs.current[index].style.display = 'block';
        myRefs.current[index].style.backgroundColor = 'rgba(25, 118, 210, 0.04)';
        myRefs.current[index].style.color = 'rgba(0, 0, 0, 0.26)';
        myRefs.current[index].style.borderColor = 'rgba(0, 0, 0, 0.26)';

        onRunDelete?.(documentId);
    };

    const datapoints = useMemo(() => {
        const possibleDatapoints = results[0]?.datapoints ?? [];

        return possibleDatapoints.map((datapoint) => ({
            id: datapoint.id,
            name: datapoint.name,
        }));
    }, [results]);

    const tableHeaders = [
        {
            label: Columns.run,
            align: 'left',
            isSortEnabled: true,
        },
        {
            label: Columns.averageProgress,
            align: 'center',
            isSortEnabled: true,
        },
        {
            label: Columns.createdAt,
            align: 'left',
            isSortEnabled: true,
        },
        ...datapoints.map((datapoint) => ({
            label: datapoint.name,
            align: 'center',
            isSortEnabled: true,
        })),
        ...(isAdmin ? [{
            label: Columns.action,
            align: 'center',
        }] : []),
    ]
        .map((header, index) => ({ ...header, id: index }));

    const convertToCells = (row, rowIndex) => [
        {
            style: {
                width: '12%',
                minWidth: '100px',
                padding: '0 0 0 16px',
            },
            data: row.name || 'Unknown',
        },
        {
            comparableValue: row.progress ?? 0,
            align: 'center',
            style: {
                minWidth: '125px',
                padding: '0',
            },
            data: row.progress ? `${row.progress.toFixed(percentPrecision)}%` : '-',
        },
        {
            comparableValue: new Date(row.added_at)
                .getTime(),
            style: {
                minWidth: '125px',
            },
            data: datetimeToFormattedString(row.added_at) ?? '',
        },
        ...datapoints.map((datapoint, index) => {
            const dp = row.datapoints.find((dp) => datapoint.id === dp.id);

            return {
                comparableValue: dp?.progress ?? 0,
                style: {
                    minWidth: '125px',
                    padding: '0 20px 0 0',
                },
                data: datapointStatusCell(dp, index),
            };
        }),
        ...(isAdmin ? [{
            style: {
                padding: 0,
            },
            align: 'center',
            data: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box key={`id${row.id}`} sx={{ position: 'relative' }}>
                        <Tooltip title="Delete">
                            <span>
                                <IconButton
                                    id={`button-id${row.id}-delete`}
                                    ref={(el) => (myRefs.current[`id${row.id}`] = el)}
                                    onClick={() => handleDelete(row.id, `id${row.id}`)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <CircularProgress
                            size={24}
                            ref={(el) => (myLoadingRefs.current[`id${row.id}`] = el)}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                                display: 'none',
                            }}
                        />
                    </Box >
                </div>
            ),
        }] : []),
    ];

    const datapointStatusCell = (datapoint, index) => {
        const isMissing = datapoint?.missing ?? false;
        const progress = datapoint?.progress ?? 0;

        if (isMissing) {
            return (
                <div className={`${styles.datapointCell} ${styles.missingDatapointCell}`}>
                    Missing
                </div>
            );
        }

        if (progress >= 100) {
            return (
                <div className={`${styles.datapointCell} ${styles.completedDatapointCell}`}>
                    Completed
                </div>
            );
        }

        return (
            <div className={`${styles.datapointCell} ${styles.percentage}`}>
                { `${progress.toFixed(percentPrecision)}%` }
            </div>
        );
    };

    const descendingComparator = (a, b, orderBy) => {
        const cellA = a.cells[orderBy];
        const cellB = b.cells[orderBy];
        const comparableValueA = cellA.comparableValue ?? cellA.data;
        const comparableValueB = cellB.comparableValue ?? cellB.data;

        if (comparableValueB < comparableValueA) {
            return -1;
        }

        if (comparableValueB > comparableValueA) {
            return 1;
        }

        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    const tableRows = useMemo(() =>
        results
            .map((row, rowIndex) => ({
                cells: convertToCells(row, rowIndex),
            }))
            .sort(getComparator(order, orderBy)),
    [results, order, orderBy]);

    return results.length > 0
        ? (
            <AvlTable
                isPaginationDisabled
                headers={tableHeaders}
                rows={tableRows}
                isSortEnabled
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
            />
        )
        : (
            <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="warning">Records not found!</Alert>
            </Stack>
        );
}

export default StatusTable;
